/**
 * "The title sequence has finished — the game's own screen is arriving now."
 *
 * WHY THIS EXISTS. Every game mounts UNDERNEATH the provider's title sequence
 * (see `ProviderIntro`) — that is the whole point of the overlay, so the game
 * boots while the mark is on screen. Which means a game's `useEffect(…, [])`
 * fires four or five seconds before the player sees anything of it. Anything
 * that should happen when the player ARRIVES — the music bed, most obviously —
 * cannot hang off mount, or it starts against a black screen and the first thing
 * they hear is the middle of a track.
 *
 * WHY IT READS THE DOM RATHER THAN KEEPING A FLAG. A module-level "done" boolean
 * survives client-side navigation, so the second game a player opens would find
 * it already true and start its music immediately, under its own intro. Testing
 * for the overlay's presence has no such memory: React commits the whole tree's
 * DOM before it runs any effect, so by the time a game's effect asks, the
 * overlay either is in the document or was never going to be.
 *
 * NO OVERLAY MEANS GO NOW. A game rendered without the wrapper — a future route,
 * a test — is not made silent by that.
 */

/** Dispatched by `ProviderIntro` when the game's screen starts being revealed. */
export const INTRO_DONE_EVENT = "provider:intro-done";

/**
 * The backstop, and why it is a poll rather than a timer.
 *
 * A plain "fire after N ms" backstop has to guess how long the sequence takes,
 * and it guesses wrong in the one case that matters. Measured on a machine slow
 * enough to be rendering in software, the hand-over landed at 29 SECONDS on Coin
 * Duel and 38 on Kamba Adeema — the main thread was starved, so every timer and
 * state update in the overlay ran late. A 9s backstop fired long before the
 * screen was handed over and started the music underneath the loading screen,
 * which is precisely the thing this module exists to prevent.
 *
 * So the backstop asks the document instead: if the overlay has actually gone,
 * the event was simply missed and it is safe to go. The ceiling exists only for
 * an overlay that never leaves at all, and is generous enough that no honest
 * sequence reaches it.
 */
const POLL_MS = 500;
const CEILING_MS = 25000;

/**
 * Run `cb` once the title sequence is on its way out, or immediately if there is
 * no title sequence to wait for.
 *
 * Returns a cancel function; call it from an effect's cleanup.
 */
export function onIntroDone(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};

  // No overlay in the document: nothing to wait for.
  if (!document.querySelector(".provider-intro")) {
    cb();
    return () => {};
  }

  let fired = false;
  let timer = 0;
  const startedAt = Date.now();

  const cleanup = () => {
    window.clearTimeout(timer);
    window.removeEventListener(INTRO_DONE_EVENT, run);
  };

  function run() {
    if (fired) return;
    fired = true;
    cleanup();
    cb();
  }

  // The backstop: go when the overlay has actually left, not when a clock says
  // it probably has. See the note on POLL_MS above.
  const tick = () => {
    if (fired) return;
    if (
      !document.querySelector(".provider-intro") ||
      Date.now() - startedAt > CEILING_MS
    ) {
      run();
      return;
    }
    timer = window.setTimeout(tick, POLL_MS);
  };
  timer = window.setTimeout(tick, POLL_MS);

  window.addEventListener(INTRO_DONE_EVENT, run, { once: true });
  return cleanup;
}
